import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Check, Copy, X, type LucideIcon } from 'lucide-react';
import { useConsole } from '../state/ConsoleProvider';

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description: string; actions?: ReactNode }) {
  return <header className="page-header"><div>{eyebrow && <p className="eyebrow">{eyebrow}</p>}<h1>{title}</h1><p>{description}</p></div>{actions && <div className="page-actions">{actions}</div>}</header>;
}

export function Badge({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'success' | 'accent' | 'error' }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

export function CopyButton({ value, label = 'Copy', iconOnly = false }: { value: string; label?: string; iconOnly?: boolean }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { notify } = useConsole();
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  useEffect(() => { setCopied(false); if (timer.current) clearTimeout(timer.current); }, [value]);
  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
      notify('Copied to clipboard.');
    } catch { notify('Clipboard unavailable. Select and copy the text manually.', 'error'); }
  }
  return <button type="button" className={`copy-button ${iconOnly ? 'icon-button' : ''}`} onClick={copy} aria-label={copied ? 'Copied' : label} title={label}>{copied ? <Check size={15} /> : <Copy size={15} />}{!iconOnly && <span>{copied ? 'Copied' : label}</span>}</button>;
}

export function MetricCard({ label, value, detail, icon: Icon }: { label: string; value: ReactNode; detail?: ReactNode; icon?: LucideIcon }) {
  return <div className="metric-card"><div className="metric-label"><span>{label}</span>{Icon && <Icon size={17} />}</div><div className="metric-value">{value}</div>{detail && <div className="metric-detail">{detail}</div>}</div>;
}

export function EmptyState({ icon: Icon, title, description, action }: { icon?: LucideIcon; title: string; description: string; action?: ReactNode }) {
  return <div className="empty-state">{Icon && <div className="empty-icon"><Icon size={24} /></div>}<h3>{title}</h3><p>{description}</p>{action}</div>;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return <label className="field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export function Modal({ title, description, children, onClose }: { title: string; description?: string; children: ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const headingId = useId();
  const descriptionId = useId();
  const initialFocus = useRef<Element | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    initialFocus.current = document.activeElement;
    const dialog = ref.current;
    dialog?.showModal();
    const close = (event: Event) => { event.preventDefault(); onCloseRef.current(); };
    dialog?.addEventListener('cancel', close);
    return () => {
      dialog?.removeEventListener('cancel', close);
      dialog?.close();
      if (initialFocus.current instanceof HTMLElement) initialFocus.current.focus();
    };
  }, []);
  return <dialog ref={ref} className="modal" aria-labelledby={headingId} aria-describedby={description ? descriptionId : undefined} onClick={event => { if (event.target === ref.current) { const rect = ref.current.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose(); } }}><div className="modal-header"><div><h2 id={headingId}>{title}</h2>{description && <p id={descriptionId}>{description}</p>}</div><button type="button" className="icon-button" onClick={onClose} aria-label="Close dialog"><X size={19} /></button></div>{children}</dialog>;
}

export function LoadingState() {
  return <div className="loading-state" role="status" aria-label="Loading console"><div className="skeleton skeleton-heading"/><div className="metric-grid">{[1,2,3,4].map(n => <div className="skeleton skeleton-metric" key={n}/>)}</div><div className="skeleton skeleton-chart"/><span className="sr-only">Loading console…</span></div>;
}

export function ErrorState({ message, retry }: { message: string; retry: () => void }) {
  return <div className="inline-error" role="alert"><p>{message}</p><button className="button button-secondary" onClick={retry}>Try again</button></div>;
}
